import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Radio, TrendingUp, TrendingDown, Minus, Trophy, ArrowLeftRight, ChevronDown,
  Star, Timer, Users,
} from "lucide-react";
import { api, formatRank } from "@/lib/api";
import { T } from "@/lib/testIds";
import { useMe } from "@/lib/useMe";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { cn } from "@/lib/utils";

const PulseDot = ({ live }) => (
  <span
    className={cn(
      "inline-block w-2 h-2 rounded-full",
      live ? "bg-totem-red animate-pulse" : "bg-totem-textFaint"
    )}
  />
);

const RankDelta = ({ delta }) => {
  if (!delta || delta === 0)
    return (
      <span className="inline-flex items-center gap-0.5 text-totem-textFaint mono text-[10px]">
        <Minus size={10} /> —
      </span>
    );
  if (delta > 0)
    return (
      <span className="inline-flex items-center gap-0.5 text-totem-green mono text-[10px]">
        <TrendingUp size={10} /> +{delta}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-0.5 text-totem-red mono text-[10px]">
      <TrendingDown size={10} /> {delta}
    </span>
  );
};

export default function Live() {
  const [tab, setTab] = useState("overall");
  return (
    <div data-testid={T.livePage} className="space-y-4 pt-1 animate-fade-up">
      <div className="flex items-center gap-2">
        <Radio className="text-totem-red animate-pulse" size={18} />
        <h2 className="display-heading text-lg">Live Rank Tracker</h2>
      </div>

      <div className="grid grid-cols-2 gap-1 p-0.5 bg-totem-surface border border-totem-border rounded-lg">
        <button
          onClick={() => setTab("overall")}
          data-testid={T.liveTabOverall}
          className={cn(
            "mono text-xs uppercase tracking-wider py-2 rounded-md transition",
            tab === "overall"
              ? "bg-totem-green text-totem-bg font-semibold"
              : "text-totem-textDim"
          )}
        >
          Overall
        </button>
        <button
          onClick={() => setTab("league")}
          data-testid={T.liveTabLeague}
          className={cn(
            "mono text-xs uppercase tracking-wider py-2 rounded-md transition",
            tab === "league"
              ? "bg-totem-green text-totem-bg font-semibold"
              : "text-totem-textDim"
          )}
        >
          Mini-league
        </button>
      </div>

      {tab === "overall" ? <OverallLive /> : <LeagueLivePicker />}
    </div>
  );
}

function OverallLive() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["live-rank"],
    queryFn: async () => (await api.get("/live/rank")).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (isLoading) return <div className="totem-card h-64 animate-pulse" />;
  if (error) return <div className="totem-card p-4 text-totem-red">{String(error.message)}</div>;

  const live = data.is_live;

  return (
    <div className="space-y-4">
      <section className={cn("totem-card p-5", live && "border-totem-red/40")}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <PulseDot live={live} />
            <span
              data-testid={T.liveStatus}
              className={cn(
                "mono uppercase text-[10px] font-semibold tracking-widest",
                live ? "text-totem-red" : "text-totem-textDim"
              )}
            >
              {live ? "LIVE" : "Not live"}
            </span>
          </div>
          <div className="mono text-[10px] uppercase text-totem-textDim">
            {data.gameweek_name}
          </div>
        </div>

        {live ? (
          <>
            <div className="flex items-end justify-between">
              <div>
                <div className="label-caps mb-1">GW live pts</div>
                <div
                  data-testid={T.liveGwPoints}
                  className="display-heading text-6xl text-totem-green leading-none"
                >
                  {data.live_points}
                </div>
                <div className="mono text-[11px] text-totem-textDim mt-2">
                  +{data.bench_points} bench
                </div>
              </div>
              <div className="text-right">
                <div className="label-caps mb-1">Live rank</div>
                <div
                  data-testid={T.liveRank}
                  className="display-heading text-2xl"
                >
                  {data.live_rank_estimate ? formatRank(data.live_rank_estimate) : "—"}
                </div>
                <RankDelta
                  delta={
                    data.live_rank_estimate && data.prev_rank
                      ? data.prev_rank - data.live_rank_estimate
                      : 0
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="totem-card-inner p-3">
                <div className="label-caps mb-1">Live total</div>
                <div className="mono font-semibold text-totem-green">
                  {data.live_total_points}
                </div>
              </div>
              <div className="totem-card-inner p-3">
                <div className="label-caps mb-1">GW avg</div>
                <div className="mono font-semibold">
                  {data.gw_average ?? "—"}
                </div>
              </div>
              <div className="totem-card-inner p-3">
                <div className="label-caps mb-1">GW top</div>
                <div className="mono font-semibold">
                  {data.gw_highest ?? "—"}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 text-xs mono">
              <span className="text-totem-textDim">
                <Timer className="inline mr-1" size={12} />
                {data.matches_in_play} in play • {data.matches_finished}/{data.matches_total} done
              </span>
              {data.active_chip && (
                <span className="text-totem-gold uppercase text-[10px] tracking-wider">
                  {data.active_chip}
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="py-4">
            <div className="mono text-[10px] uppercase text-totem-textDim">Overall</div>
            <div className="display-heading text-4xl text-totem-text">
              {formatRank(data.user?.overall_rank)}
            </div>
            <div className="mono text-xs text-totem-textDim mt-2">
              {data.user?.overall_points ?? 0} pts
            </div>
            <div className="mt-4 text-sm text-totem-textDim leading-relaxed">
              {data.message}
            </div>
          </div>
        )}
      </section>

      {live && data.captain && (
        <section
          data-testid={T.liveCaptainCard}
          className="totem-card p-4 border-totem-gold/40"
          style={{ boxShadow: "0 0 20px rgba(227,195,43,0.12)" }}
        >
          <div className="flex items-center gap-3">
            <PlayerAvatar player={data.captain} size={54} showRing />
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <Star size={14} className="text-totem-gold" fill="currentColor" />
                <span className="mono uppercase text-[10px] text-totem-gold tracking-wider">
                  Captain live
                </span>
              </div>
              <div className="display-heading text-lg mt-0.5">
                {data.captain.web_name}
              </div>
              <div className="mono text-[10px] text-totem-textDim uppercase">
                {data.captain.team_short} • {data.captain.position}
              </div>
            </div>
            <div className="text-right">
              <div className="display-heading text-3xl text-totem-gold">
                {data.captain.captain_live_total}
              </div>
              <div className="mono text-[9px] uppercase text-totem-textDim">
                {data.captain.live_points} × {data.captain.captain_multiplier}
              </div>
            </div>
          </div>
        </section>
      )}

      {!live && (
        <div className="totem-card p-4 text-center border-totem-amber/30">
          <p className="text-sm text-totem-textDim leading-relaxed">
            When GW matches kick off, live rank uses the FPL live endpoint plus rank benchmarks
            to estimate where you sit in real time.
          </p>
        </div>
      )}
    </div>
  );
}

function LeagueLivePicker() {
  const [selected, setSelected] = useState(null);
  const { data: leagues } = useQuery({
    queryKey: ["leagues-mine"],
    queryFn: async () => (await api.get("/leagues/mine")).data,
    staleTime: 60_000,
  });
  const classic = leagues?.classic || [];
  const active = selected || classic[0];

  return (
    <div className="space-y-3">
      <div className="relative">
        <select
          data-testid={T.liveLeagueSelect}
          value={active?.id || ""}
          onChange={(e) => {
            const id = Number(e.target.value);
            setSelected(classic.find((l) => l.id === id) || null);
          }}
          className="w-full appearance-none bg-totem-surfaceElev border border-totem-border rounded-lg pl-4 pr-10 py-3 mono text-sm focus:outline-none focus:border-totem-green/60"
        >
          {classic.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <ChevronDown
          className="absolute right-3 top-1/2 -translate-y-1/2 text-totem-textDim"
          size={16}
        />
      </div>

      {active ? <LeagueLiveTable leagueId={active.id} /> : (
        <div className="totem-card p-6 text-center text-totem-textDim">
          No leagues linked to your manager.
        </div>
      )}
    </div>
  );
}

function LeagueLiveTable({ leagueId }) {
  const { data: meData } = useMe();
  const myManagerId = meData?.manager?.id;
  const { data, isLoading, error } = useQuery({
    queryKey: ["live-league", leagueId],
    queryFn: async () => (await api.get(`/live/league/${leagueId}`)).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  if (isLoading) return <div className="totem-card h-64 animate-pulse" />;
  if (error) return <div className="totem-card p-4 text-totem-red">{String(error.message)}</div>;
  const rows = data.standings || [];
  return (
    <section data-testid={T.liveLeagueTable} className="totem-card overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-totem-borderSoft">
        <div className="flex items-center gap-2">
          <Trophy size={14} className="text-totem-green" />
          <div className="min-w-0">
            <div className="font-semibold truncate">{data.league?.name}</div>
            <div className="mono text-[10px] uppercase text-totem-textDim">
              {data.is_live ? "Live overlay" : "Static — waiting for GW"}
            </div>
          </div>
        </div>
        <PulseDot live={data.is_live} />
      </div>

      <table className="w-full text-sm">
        <thead className="bg-totem-surface">
          <tr>
            <th className="text-left px-2 py-2 label-caps">Rank</th>
            <th className="text-left px-2 py-2 label-caps">Manager</th>
            <th className="text-right px-2 py-2 label-caps">GW</th>
            <th className="text-right px-2 py-2 label-caps">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isMe = r.entry === myManagerId;
            return (
              <tr
                key={r.entry}
                className={cn(
                  "border-t border-totem-borderSoft",
                  isMe && "bg-totem-green/5"
                )}
              >
                <td className="px-2 py-2.5">
                  <div className="flex items-center gap-1">
                    <span className="mono">{r.live_rank}</span>
                    <RankDelta delta={r.rank_delta} />
                  </div>
                </td>
                <td className="px-2 py-2.5 min-w-0">
                  <div className={cn("font-semibold truncate", isMe && "text-totem-green")}>
                    {r.entry_name}
                  </div>
                  <div className="mono text-[10px] uppercase text-totem-textDim truncate">
                    {r.player_name}
                  </div>
                </td>
                <td className="px-2 py-2.5 text-right mono text-totem-green">
                  {r.live_event_total}
                </td>
                <td className="px-2 py-2.5 text-right mono font-semibold">
                  {r.live_total}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="p-6 text-center text-totem-textDim text-sm">
                No standings yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rows.length >= 30 && (
        <div className="text-[11px] mono uppercase text-totem-textFaint text-center py-2 border-t border-totem-borderSoft">
          Top 30 shown • auto-refresh 60s
        </div>
      )}
    </section>
  );
}
